import { useState, useEffect, useRef } from "react";
import { useFormik } from "formik";
import { z } from "zod";
import { toFormikValidate } from "zod-formik-adapter";
import { Input, TextArea } from "@/components/UI";
import PhoneInput from "@/components/UI/PhoneInput";
import { useLocation } from "react-router-dom";
import useMetaPixel from "@/custom_hooks/useMetaPixel";
import useNewslettersSubscription from "@/custom_hooks/useNewslettersSubcription";
import { pushEvent } from "@/utils/gtm";
import bioDataImg from "@/assets/images/journeyImages/2892a8f408f857c96924fd8f89b0794745af5039.jpg";
import interpretImg from "@/assets/images/journeyImages/08f7915c1b58462edf2d43e6a9305ec959bf0398.jpg";
import personalPlanImg from "@/assets/images/journeyImages/3814e0c248f39cf6ddc71377ddda8bdf911d1d94.jpg";
import { Button } from "../UI/Buttons/Button";

import { isPhoneValid } from "@/utils/formValidator";

const validationSchema = z
  .object({
    firstName: z.string().min(1, "Vardas yra privalomas"),
    lastName: z.string().min(1, "Pavardė yra privaloma"),
    email: z
      .string()
      .email("Neteisingas el. pašto adresas")
      .min(1, "El. paštas yra privalomas"),
    phone: z
      .string()
      .min(1, "Telefono numeris yra privalomas")
      .refine((v) => isPhoneValid(v), "Neteisingas telefono numerio formatas"),
    phoneCountry: z.string().optional(),
    comment: z.string().optional(),
    source: z.string().optional(),
  })
  .refine((data) => isPhoneValid(data.phone, data.phoneCountry), {
    message: "Telefono numeris neatitinka pasirinktos šalies",
    path: ["phone"],
  });

type FormValues = z.infer<typeof validationSchema>;

const ConsultationForm = () => {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);
  const [errorMsg, setErrorMsg] = useState<string>("");
  const [successMsg, setSuccessMsg] = useState<string>("");

  const hasStartedFillingRef = useRef(false);

  const baseApiUrl = import.meta.env.VITE_BASE_API_URL;
  const metaPixel = useMetaPixel();
  const location = useLocation();

  const { isValid } = useNewslettersSubscription(
    setErrorMsg,
    setSuccessMsg,
    setIsSubmitting,
  );

  const formik = useFormik<FormValues>({
    initialValues: {
      firstName: "",
      lastName: "",
      email: "",
      phone: "",
      phoneCountry: "LT",
      comment: "",
      source: "",
    },
    validate: toFormikValidate(validationSchema),
    onSubmit: async (values) => {
      setErrorMsg("");
      setSuccessMsg("");
      const fullName = `${values.firstName} ${values.lastName}`.trim();

      const subscriptionDataError = isValid(values.email, false);
      if (subscriptionDataError) {
        setErrorMsg(subscriptionDataError);
        return;
      }
      if (values.phone.trim() === "") {
        setErrorMsg("Įveskite telefono nr.");
        return;
      }

      setIsSubmitting(true);
      try {
        const payload = {
          full_name: fullName,
          email: values.email,
          phone: values.phone,
          comment: values.comment || "",
          source: values.source || "",
        };

        const res: any = await fetch(baseApiUrl + "/consultations", {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Accept: "application/json",
          },
          body: JSON.stringify(payload),
        });

        const data = await res.json();
        if (res.ok) {
          metaPixel.track15minConsult(
            location.hash === "#konsultacija" ? "advertorial" : "direct",
          );
          pushEvent("15_min_consultation_completed");

          if (data.message) {
            setSuccessMsg(data.message);
          } else {
            setSuccessMsg("Registracija konsultacijai sėkminga.");
          }
          setIsSuccess(true);
        } else if (res.status === 500) {
          setErrorMsg("Įvyko sistemos klaida, pabandykite vėliau");
        } else if (res.status === 422 && data.message) {
          setErrorMsg(data.message);
        } else {
          setErrorMsg("Įvyko sistemos klaida, pabandykite vėliau");
        }
      } catch (error: any) {
        setErrorMsg("Įvyko sistemos klaida, pabandykite vėliau");
      } finally {
        setIsSubmitting(false);
      }
    },
  });

  useEffect(() => {
    if (formik.dirty && !hasStartedFillingRef.current) {
      pushEvent("15_min_consultation_not_completed");
      hasStartedFillingRef.current = true;
    }
  }, [formik.dirty]);

  if (isSuccess) {
    return (
      <div className="flex w-full flex-col items-center justify-center space-y-4 rounded-xl bg-white p-10 text-center shadow-sm">
        <h3 className="text-2xl font-medium text-gray-900">Ačiū!</h3>
        <p className="text-gray-700">
          {successMsg ||
            "Jūsų registracija priimta. Susisieksime su jumis netrukus."}
        </p>
      </div>
    );
  }

  return (
    <div className="flex w-full flex-col gap-10 md:gap-[60px]">
      <form
        onSubmit={formik.handleSubmit}
        className="flex w-full flex-col gap-5"
      >
        <div className="flex w-full flex-col gap-5 md:flex-row">
          <div className="flex w-full flex-col">
            <Input
              id="firstName"
              name="firstName"
              placeholder="Jūsų vardas"
              label="Vardas *"
              value={formik.values.firstName}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              className="w-full"
            />
            {formik.touched.firstName && formik.errors.firstName ? (
              <div className="mt-1 text-sm text-red-500">
                {formik.errors.firstName}
              </div>
            ) : null}
          </div>

          <div className="flex w-full flex-col">
            <Input
              id="lastName"
              name="lastName"
              placeholder="Jūsų pavardė"
              label="Pavardė *"
              value={formik.values.lastName}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              className="w-full"
            />
            {formik.touched.lastName && formik.errors.lastName ? (
              <div className="mt-1 text-sm text-red-500">
                {formik.errors.lastName}
              </div>
            ) : null}
          </div>
        </div>

        <div className="flex w-full flex-col gap-5 md:flex-row md:gap-5">
          <div className="flex w-full flex-col">
            <Input
              id="email"
              name="email"
              type="email"
              placeholder="Jūsų el. paštas"
              label="El. pašto adresas *"
              value={formik.values.email}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              className="w-full"
            />
            {formik.touched.email && formik.errors.email ? (
              <div className="mt-1 text-sm text-red-500">
                {formik.errors.email}
              </div>
            ) : null}
          </div>

          <div className="flex w-full flex-col">
            <PhoneInput
              id="phone"
              name="phone"
              label="Tel. numeris *"
              value={formik.values.phone}
              onChange={(val) => formik.setFieldValue("phone", val)}
              onCountryChange={(c) => formik.setFieldValue("phoneCountry", c ?? "LT")}
              onBlur={formik.handleBlur}
              className="w-full"
              error={
                formik.touched.phone && formik.errors.phone
                  ? String(formik.errors.phone)
                  : undefined
              }
            />
          </div>
        </div>

        <div className="flex w-full flex-col">
          <TextArea
            id="comment"
            name="comment"
            placeholder="Praneškite mums, kuo galime padėti — dėl narysčių, registracijos keliems asmenims ar bendrų klausimų."
            label="Komentaras"
            value={formik.values.comment}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            className="w-full"
            inputClassName="min-h-[120px] border border-line-strong"
            labelClassName="flex justify-between items-center w-full after:text-xs after:text-gray-400 after:font-normal"
          />
        </div>

        <div className="mt-5 flex w-full flex-col items-center gap-5 md:gap-7.5">
          {errorMsg && (
            <p className="text-center text-sm font-medium text-red-500">
              {errorMsg}
            </p>
          )}
          <Button
            type="submit"
            disabled={isSubmitting}
            variant="ink"
            className="h-[63px] w-full"
            hoverGradientLight
            onHover
          >
            {isSubmitting ? "Siunčiama..." : "Tęsti"}
          </Button>
          <p className="w-full text-center text-sm leading-[1.4] text-gray-500">
            Tęsdami sutinkate gauti su Telomera.lt susijusius el. laiškus, kaip
            nurodyta mūsų Privatumo politikoje.
          </p>
        </div>
      </form>

      {/* What's Included Section */}
      <div className="mx-auto flex w-full flex-col items-center justify-center gap-10 md:w-150">
        <div className="flex w-[100%] items-center justify-center gap-[30px]">
          <div className="bg-ink h-px flex-1"></div>
          <p className="text-ink text-center text-sm font-normal whitespace-nowrap">
            Ką gaunate su „Telomera“
          </p>
          <div className="bg-ink h-px flex-1"></div>
        </div>

        <div className="flex w-full flex-col gap-[30px]">
          {/* Bulletpoint 1 */}
          <div className="flex w-full flex-col gap-6">
            <div className="flex w-full items-center gap-5">
              <div className="flex h-[90px] w-[90px] shrink-0 items-center justify-center overflow-hidden rounded-[6px] bg-gray-50 shadow-sm">
                <img
                  src={bioDataImg}
                  alt="Biologiniai duomenys"
                  className="h-full w-full object-cover"
                />
              </div>
              <div className="flex flex-1 flex-col gap-2">
                <p className="text-[16px] leading-[1.2] font-normal text-[#212121]">
                  10 000+ vertinamų sveikatos rodiklių
                </p>
                <p className="text-[14px] leading-[1.4] font-normal text-[#666666]">
                  Surenkama atliekant viso klinikinio genomo analizę, VO₂ max
                  tyrimą, kūno sudėties analizę, išplėstinius kraujo tyrimus ir
                  kitus vertinimus.
                </p>
              </div>
            </div>
            <div className="h-px w-full border-t border-dashed border-gray-200" />
          </div>

          {/* Bulletpoint 2 */}
          <div className="flex w-full flex-col gap-6">
            <div className="flex w-full items-center gap-5">
              <div className="flex h-[90px] w-[90px] shrink-0 items-center justify-center overflow-hidden rounded-[6px] bg-gray-50 shadow-sm">
                <img
                  src={interpretImg}
                  alt="Ekspertų interpretacija"
                  className="h-full w-full object-cover"
                />
              </div>
              <div className="flex flex-1 flex-col gap-2">
                <p className="text-[16px] leading-[1.2] font-normal text-[#212121]">
                  Gydytojų ekspertų konsultacijos aiškiam sveikatos rezultatų
                  supratimui
                </p>
                <p className="text-[14px] leading-[1.4] font-normal text-[#666666]">
                  Konsultacijų metu nuosekliai aptariami jūsų rezultatai,
                  svarbiausios rizikos ir konkretūs žingsniai sveikatai gerinti.
                </p>
              </div>
            </div>
            <div className="h-px w-full border-t border-dashed border-gray-200" />
          </div>

          {/* Bulletpoint 3 */}
          <div className="flex w-full flex-col gap-6">
            <div className="flex w-full items-center gap-5">
              <div className="flex h-[90px] w-[90px] shrink-0 items-center justify-center overflow-hidden rounded-[6px] bg-gray-50 shadow-sm">
                <img
                  src={personalPlanImg}
                  alt="Your Personal Telos"
                  className="h-full w-full object-cover"
                />
              </div>
              <div className="flex flex-1 flex-col gap-2">
                <p className="text-[16px] leading-[1.2] font-normal text-[#212121]">
                  Nuoseklus palaikymas siekiant ilgalaikių sveikatos pokyčių
                </p>
                <p className="text-[14px] leading-[1.4] font-normal text-[#666666]">
                  Ekspertų rekomendacijos ir nuolatinis palaikymas dėl ligų
                  valdymo, mitybos, fizinio aktyvumo, gyvensenos ir pakartotinių
                  tyrimų padeda kryptingai judėti savo tikslų link.
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ConsultationForm;
